/**
 * /kiosk/klassenbuch — teacher chip-tap entry to Klassenbuch.
 *
 * Flow:
 *   1. Idle  — "Bitte Chip auf Lesegerät tippen", listens for chip tap
 *   2. Auth  — RPC call in flight
 *   3. Greet — "Hallo Frau X" + list of offene Einträge sorted by overdue-ness
 *   4. Error — flash error overlay, auto-return to idle after 3 s
 *
 * Idle auto-logout returns the page to state 1 after 60 s of inactivity so
 * the next teacher doesn't see the previous one's data.
 *
 * Two auth modes — useChipLoginCall picks at call time:
 *   • Paired kiosk device → fw_kiosk_jwt in localStorage, kiosk supabase client
 *   • Dev / admin browser tab → authenticated user session, RTK Query
 *
 * If a kiosk JWT exists but is expired, useEffect at mount nukes it and
 * redirects to /kiosk/setup so the device can re-pair. useKioskTokenRefresh
 * keeps a healthy JWT alive in the paired-kiosk case (24h refresh threshold).
 */
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { rootApi } from "../../lib/api/rootApi";
import { GreetScreen } from "../../features/klassenbuch-kiosk/components/GreetScreen";
import { KioskCloseButton } from "../../features/klassenbuch-kiosk/components/KioskCloseButton";
import { useChipListener } from "../../features/klassenbuch-kiosk/hooks/useChipListener";
import { useChipLoginCall } from "../../features/klassenbuch-kiosk/hooks/useChipLoginCall";
import { useIdleLogout } from "../../features/klassenbuch-kiosk/hooks/useIdleLogout";
import { useKioskResetPairingListener } from "../../features/klassenbuch-kiosk/hooks/useKioskResetPairingListener";
import type { KioskLoginSuccess, KnownReaderUsbDevice } from "../../features/klassenbuch-kiosk/types/chipTypes";
import {
  clearKioskSession,
  decodeJwtPayload,
  getKioskToken,
} from "./utils/kioskUtils";
import { useKioskHeartbeat } from "./hooks/useKioskHeartbeat";
import { useKioskTokenRefresh } from "./hooks/useKioskTokenRefresh";

/** Total idle timeout — 3 minutes. The visible countdown bar only appears
 *  in the last minute (see COUNTDOWN_VISIBLE_MS in GreetScreen). */
const IDLE_TIMEOUT_MS = 3 * 60_000;
const ERROR_DISPLAY_MS = 3_000;

type ScreenState =
  | { kind: "idle" }
  | { kind: "authenticating" }
  | { kind: "greet"; session: KioskLoginSuccess; lastUid: string }
  | { kind: "error"; message: string };

const ERROR_LABELS: Record<string, string> = {
  unknown_chip: "Chip nicht registriert. Bitte im Sekretariat melden.",
  user_not_in_school: "Chip gehört zu einer anderen Schule.",
  invalid_chip_uid: "Ungültiges Chip-Format.",
};

export default function KioskKlassenbuchPage() {
  const [screen, setScreen] = useState<ScreenState>({ kind: "idle" });
  const callChipLogin = useChipLoginCall();
  const dispatch = useDispatch();

  /**
   * Reset every teacher-scoped piece of in-memory state. Called from:
   *   - The floating close button (KioskCloseButton.onBeforeClose)
   *   - The idle-logout timeout
   *
   * Wipes:
   *   - React `screen` state back to idle (no teacher name, no session)
   *   - RTK Query cache (lessons, attendance, day notes, etc.)
   *
   * Does NOT touch the kiosk JWT in localStorage — that's the machine
   * identity, not a teacher session. We want it to persist across
   * teacher logouts and app restarts.
   */
  const wipeSessionState = useCallback(() => {
    setScreen({ kind: "idle" });
    dispatch(rootApi.util.resetApiState());
  }, [dispatch]);

  // Validate any persisted kiosk JWT on mount — expired tokens get nuked and
  // the device kicked back to /kiosk/setup for re-pairing. Tokens with no
  // allowed_views entry for "klassenbuch" are also rejected so a misrouted
  // device doesn't land in the wrong app.
  useEffect(() => {
    const jwt = getKioskToken();
    if (!jwt) return;
    const payload = decodeJwtPayload(jwt);
    const nowSec = Date.now() / 1000;
    const expired = !payload || (payload.exp != null && payload.exp <= nowSec);
    const wrongView =
      payload?.allowed_views != null &&
      Array.isArray(payload.allowed_views) &&
      !payload.allowed_views.includes("klassenbuch");
    if (expired || wrongView) {
      clearKioskSession(); // wipes localStorage + replaces location → /kiosk/setup
    }
  }, []);

  // Keep the JWT fresh while running as a paired device (no-op otherwise).
  useKioskTokenRefresh();

  // Report this kiosk as online every 30 s (updates structure_monitor_devices
  // .last_seen_at). Without this the Klassenbuch kiosk shows "Offline / Nie"
  // in the monitor admin UI even while running — the substitution/FlexBand
  // views already do this, the Klassenbuch view was missing it.
  useKioskHeartbeat();

  // Wire up the tray-menu "Kopplung zurücksetzen…" flow: when main fires
  // the IPC, call fn_kiosk_revoke_self with the still-valid JWT so the
  // server marks this device inactive BEFORE main wipes local state.
  // No-op outside the Electron shell.
  useKioskResetPairingListener();

  const handleTap = useCallback(
    async (uid: string) => {
      if (screen.kind === "authenticating") return;
      setScreen({ kind: "authenticating" });
      try {
        const result = await callChipLogin(uid);
        if (result.ok) {
          setScreen({ kind: "greet", session: result, lastUid: uid });
        } else {
          setScreen({
            kind: "error",
            message: ERROR_LABELS[result.error] ?? `Fehler: ${result.error}`,
          });
        }
      } catch (e) {
        const msg = (e as { error?: { message?: string }; message?: string })?.error?.message
          ?? (e as { message?: string })?.message
          ?? "Verbindung fehlgeschlagen";
        setScreen({ kind: "error", message: msg });
      }
    },
    [callChipLogin, screen.kind],
  );

  // Auto-return to idle after an error flash.
  useEffect(() => {
    if (screen.kind !== "error") return;
    const t = setTimeout(() => setScreen({ kind: "idle" }), ERROR_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [screen]);

  // Idle auto-logout — only when somebody is signed in. On timeout we
  // both reset state AND hide the window to tray; that way the next
  // chip-tap re-opens the kiosk fresh on the idle screen, instead of
  // leaving a stale "Bitte Chip tippen" prompt visible to the room.
  const { remainingMs: idleRemainingMs } = useIdleLogout({
    enabled: screen.kind === "greet",
    timeoutMs: IDLE_TIMEOUT_MS,
    onTimeout: () => {
      wipeSessionState();
      window.kiosk?.hideToTray?.().catch(() => { /* fire-and-forget */ });
    },
  });

  const { status, readerName, readerError, lastTap } = useChipListener({
    onTap: (e) => handleTap(e.uid),
  });

  const channelLabel =
    status === "electron-ready" ? "PC/SC"
    : status === "no-bridge" ? "kein Kiosk-Shell"
    : status === "disabled" ? "deaktiviert"
    : "initialisiert";

  // The kiosk requires a PC/SC reader to be useful at all — without it
  // there's no way for a teacher to log in. Surface this prominently as a
  // blocking state on the idle screen, NOT as a small footer note.
  const readerBlocked =
    status === "no-bridge" ||
    !!readerError ||
    (status === "electron-ready" && !readerName);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <KioskCloseButton onBeforeClose={wipeSessionState} />
      {screen.kind === "idle" && (
        <IdleScreen
          channelLabel={channelLabel}
          readerName={readerName}
          readerError={readerError}
          lastUid={lastTap?.uid ?? null}
          blocked={readerBlocked}
          status={status}
        />
      )}
      {screen.kind === "authenticating" && <AuthenticatingScreen />}
      {screen.kind === "greet" && (
        <GreetScreen
          session={screen.session}
          idleRemainingMs={idleRemainingMs}
        />
      )}
      {screen.kind === "error" && <ErrorOverlay message={screen.message} />}

      {/* Tiny status footer always present for ops visibility */}
      <footer className="absolute bottom-3 right-4 text-xs text-slate-700 font-mono select-none">
        Klassenbuch-Kiosk · {channelLabel}
      </footer>
    </div>
  );
}

// ─── Screens ─────────────────────────────────────────────────────────────────

function IdleScreen({
  channelLabel,
  readerName,
  readerError,
  lastUid,
  blocked,
  status,
}: {
  channelLabel: string;
  readerName: string | null;
  readerError: string | null;
  lastUid: string | null;
  blocked: boolean;
  status: string;
}) {
  // No PC/SC reader → kiosk is inoperable. Show a blocking message instead
  // of the cheerful "tap your chip" prompt; the latter would mislead
  // teachers into tapping a chip that can't be read.
  if (blocked) {
    return <ReaderUnavailableScreen status={status} readerError={readerError} />;
  }

  // Decorative floating background icons and an animate-ping pulse used to
  // sit here, but they ran transform-based animations at 60 FPS forever,
  // burning ~5-8% sustained CPU per renderer on the school kiosks (Acer
  // N5030 / Lenovo i5-8250U). The kiosk sits on the idle screen for hours,
  // so removing them — without changing the visual layout meaningfully —
  // is the cheapest single CPU win.
  return (
    <div className="flex-1 relative flex flex-col items-center justify-center p-8 bg-gray-100 text-center overflow-hidden">
      <div className="relative z-10 max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="flex justify-center">
            <img
              src="/flexwise-logo-with-tagline.png"
              alt="FlexWise – Flexible Tools für smarte Schulen"
              className="w-auto"
              style={{ height: "94px" }}
            />
          </div>

          <div className="relative mx-auto w-24 h-24 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="text-blue-600 relative z-10">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M7 9h.01M11 9c0 2-2 4-2 4M15 7c2 2 2 8 0 10" />
            </svg>
          </div>

          <div>
            <h1 className="text-4xl font-light text-gray-900 leading-tight">
              Bitte Chip auf das Lesegerät tippen
            </h1>
          </div>

          <div className="text-sm text-gray-500">
            Kanal: {channelLabel}
            {readerName ? <> · {readerName}</> : null}
            {lastUid ? <> · letzter Tap: {lastUid}</> : null}
          </div>

          {readerError ? (
            <div className="text-sm text-rose-600">⚠ {readerError}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Blocking screen when no PC/SC reader is available. Flavours:
 *   - no-bridge: page opened in a browser (not the kiosk shell)
 *   - driver-missing: USB device matches a known reader VID, but PC/SC
 *     hasn't enumerated it → driver isn't installed. Show vendor name +
 *     driver download link.
 *   - readerError: main process reported an explicit error (often
 *     SCardSvr not running on Windows)
 *   - electron-ready but no readerName + no USB match: shell up, but
 *     nothing plugged in
 */
function ReaderUnavailableScreen({
  status,
  readerError,
}: {
  status: string;
  readerError: string | null;
}) {
  const isBrowserTab = status === "no-bridge";

  // Poll the USB probe every 4s while we're stuck on this screen. As soon
  // as a known reader appears (or its driver lands), we surface that fact.
  const [knownUsb, setKnownUsb] = useState<KnownReaderUsbDevice[]>([]);
  useEffect(() => {
    if (isBrowserTab) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const result = await window.kiosk?.probeUsbReaders?.();
        if (!cancelled && Array.isArray(result)) setKnownUsb(result);
      } catch {
        // best-effort probe
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isBrowserTab]);

  const driverMissing = !isBrowserTab && knownUsb.length > 0;
  const firstKnown = knownUsb[0];

  const heading = isBrowserTab
    ? "Kiosk-App nicht erkannt"
    : driverMissing
      ? `${firstKnown.name}: Treiber fehlt`
      : "Kein RFID-Leser verbunden";

  return (
    <div className="flex-1 relative flex flex-col items-center justify-center p-8 bg-amber-50 text-center overflow-hidden">
      <div className="relative z-10 max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl p-10 space-y-6 border border-amber-200">
          <div className="flex justify-center">
            <img
              src="/flexwise-logo-with-tagline.png"
              alt="FlexWise"
              className="w-auto opacity-70"
              style={{ height: "70px" }}
            />
          </div>

          <div className="relative mx-auto w-24 h-24 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center text-amber-700">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          </div>

          <h1 className="text-3xl font-light text-gray-900 leading-tight">
            {heading}
          </h1>

          {isBrowserTab && (
            <p className="text-base text-gray-600 leading-relaxed">
              Diese Seite ist in einem normalen Browser geöffnet. Bitte die
              Flexwise Klassenbuch Kiosk-App auf dem Kiosk-PC starten.
            </p>
          )}

          {!isBrowserTab && driverMissing && (
            <>
              <p className="text-base text-gray-700 leading-relaxed">
                Windows erkennt einen <strong>{firstKnown.name}</strong>-Leser
                an USB, aber der Treiber ist nicht installiert.
              </p>
              <a
                href={firstKnown.driverUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-sm transition-colors"
              >
                Treiber herunterladen ↗
              </a>
              <p className="text-xs text-gray-500">
                Nach Installation des Treibers wird der Leser automatisch
                erkannt — kein Neustart der App nötig.
              </p>
            </>
          )}

          {!isBrowserTab && !driverMissing && (
            <p className="text-base text-gray-600 leading-relaxed">
              {readerError
                ? readerError
                : "Bitte stecken Sie einen PC/SC RFID-Leser an (z.B. ACR122U, Reiner SCT cyberJack, Identiv uTrust). Der Kiosk ist ohne Leser nicht einsatzbereit."}
            </p>
          )}

          <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Der Kiosk prüft alle paar Sekunden, ob ein Leser verfügbar ist. Sobald
            er erkannt wird, schaltet diese Seite automatisch zur
            Chip-Aufforderung um.
          </p>
        </div>
      </div>
    </div>
  );
}

function AuthenticatingScreen() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-6 text-slate-300">
      <div className="w-12 h-12 rounded-full border-4 border-amber-300/30 border-t-amber-300 animate-spin" />
      <p className="text-sm">prüfe Chip…</p>
    </div>
  );
}

function ErrorOverlay({ message }: { message: string }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center px-12">
      <div className="w-32 h-32 rounded-full bg-rose-900/30 border-2 border-rose-700 flex items-center justify-center text-rose-300 text-6xl">
        !
      </div>
      <p className="text-xl text-rose-300 max-w-lg">{message}</p>
      <p className="text-xs text-slate-500">zurück zum Start in 3 Sek.</p>
    </div>
  );
}
